import { FC } from 'react';
import { Button, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useForm } from '@tanstack/react-form';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import BaseConfig from './BaseConfig';
import useCompanyId from '../../../hooks/useCompanyId';
import { usePrices, useResellerPrices } from '../../../stores/FetcherStores/PricesFetcher';
import { useCreateBillingItemMutation } from '../../../stores/FetcherStores/BillingItemFetcher';
import { BillingItemType, SubscriptionBillingItem } from '../../../model/BillingItem';
import { Money } from '../../../utils/currency';
import { useGetCompany } from '../../../stores/FetcherStores/CompanyFetcher';

interface UserPermissionsConfigProps {
  onClose: () => void;
}

const UserPermissionsConfig: FC<UserPermissionsConfigProps> = observer(({ onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useGetCompany(companyId);
  const {
    mutateAsync: createBillingItem,
    isLoading: isCreating,
    error,
  } = useCreateBillingItemMutation();
  const { data: prices } = usePrices();
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');
  const form = useForm({
    defaultValues: {
      startDate: DateTime.now(),
      endDate: null as DateTime | null,
      prepaidPeriod: 1,
    },
    onSubmit: async ({ value }) => {
      const billingItem: Omit<
        SubscriptionBillingItem,
        'id' | 'createdAt' | 'createdBy' | 'discount' | 'taxRate'
      > = {
        type: BillingItemType.SUBSCRIPTION,
        subscriptionId: 'USER_PERMISSIONS',
        startDate: value.startDate.toISO(),
        endDate: value.endDate?.toISO() || undefined,
        unitPrice:
          resellerPrices?.subscriptions.USER_PERMISSIONS.price ??
          prices?.subscriptions.USER_PERMISSIONS.price ??
          new Money(0),
        isDemo: false,
        quantity: 1,
      };
      await createBillingItem(companyId, billingItem)
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  return (
    <BaseConfig
      title="Användarbehörigheter paket"
      description="Paket för att hantera användarbehörigheter och roller i systemet. I Användarbehörigheter paketet ingår: Rollbaserad åtkomstkontroll, Anpassade behörigheter, Användarhantering, Säkerhetsloggar med mera."
      price={`${prices?.subscriptions.USER_PERMISSIONS.price.format()}/mån`}
      resellerPrice={
        resellerPrices ? `${resellerPrices?.subscriptions.USER_PERMISSIONS.price.format()}/mån` : ''
      }
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      errorMessage={
        error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={form.state.isSubmitting || isCreating}
            sx={{
              backgroundColor: '#1db954',
              '&:hover': {
                backgroundColor: '#1a9f4a',
              },
            }}
          >
            {form.state.isSubmitting || isCreating
              ? 'Aktiverar...'
              : 'Aktivera Användarbehörigheter'}
          </Button>
        </>
      }
    >
      <form.Field name="startDate">
        {(field) => (
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Startdatum"
              value={field.state.value}
              minDate={DateTime.now()}
              onChange={(date) => field.handleChange(date ?? DateTime.now())}
              format="yyyy-MM-dd"
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: field.state.meta.errors.join(', ') || 'Välj startdatum',
                  error: field.state.meta.errors.length > 0,
                },
              }}
            />
          </Grid>
        )}
      </form.Field>

      <form.Field name="endDate">
        {(field) => (
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Slutdatum (valfritt)"
              value={field.state.value}
              minDate={DateTime.now().plus({ days: 1 })}
              onChange={(date) => field.handleChange(date)}
              openTo="month"
              views={['year', 'month', 'day']}
              format="yyyy-MM-dd"
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: field.state.meta.errors.join(', ') || 'Lämna tomt för löpande',
                  error: field.state.meta.errors.length > 0,
                },
                field: { clearable: true, onClear: () => field.handleChange(null) },
              }}
            />
          </Grid>
        )}
      </form.Field>
    </BaseConfig>
  );
});

export default UserPermissionsConfig;
